/**
 * Multi-service catalog with collapsible categories + free-text search.
 *
 * The whole thing is one scrollable list — no extra "step" indirection,
 * which matches the alteg.io / Booksy menu pattern and tests significantly
 * better than the linear wizard the marketplace flow uses. Each service is
 * an add/remove toggle wired into the shared booking context, so the cart
 * (rendered elsewhere) reacts instantly.
 *
 * Categories collapse by default to keep the initial paint short; the
 * first category opens on mount so users always see at least one set of
 * services without scrolling or clicking.
 */

import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSalonBooking } from "./SalonBookingContext";
import { formatDuration, formatPrice, pickLocalized } from "./salonTheme";

function HorizontalScrollArea({ palette, children, ariaLabel }) {
  const scrollerRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateEdges = () => {
    const node = scrollerRef.current;
    if (!node) return;
    const max = node.scrollWidth - node.clientWidth;
    setCanLeft(node.scrollLeft > 4);
    setCanRight(node.scrollLeft < max - 4);
  };

  useEffect(() => {
    updateEdges();
    const node = scrollerRef.current;
    if (!node) return undefined;

    node.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    return () => {
      node.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
      if (hoverTimerRef.current) window.clearInterval(hoverTimerRef.current);
    };
  }, [children]);

  const scroll = (direction, amount = 220) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * amount, behavior: "smooth" });
    window.setTimeout(updateEdges, 240);
  };

  const startHoverScroll = (direction) => {
    scroll(direction, 120);
    if (hoverTimerRef.current) window.clearInterval(hoverTimerRef.current);
    hoverTimerRef.current = window.setInterval(() => scroll(direction, 90), 140);
  };

  const stopHoverScroll = () => {
    if (hoverTimerRef.current) {
      window.clearInterval(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const arrowBase = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 38,
    height: 38,
    borderRadius: "50%",
    border: palette.border,
    background: palette.surface,
    color: palette.accent,
    boxShadow: palette.shadowSoft,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 2,
    fontSize: "1rem",
  };

  return (
    <div style={{ position: "relative" }}>
      {canLeft ? (
        <button
          type="button"
          aria-label={ariaLabel ? `${ariaLabel}: left` : "Scroll left"}
          onClick={() => scroll(-1)}
          onMouseEnter={() => startHoverScroll(-1)}
          onMouseLeave={stopHoverScroll}
          onFocus={() => startHoverScroll(-1)}
          onBlur={stopHoverScroll}
          style={{ ...arrowBase, left: 2 }}
        >
          <i className="las la-angle-left" aria-hidden="true" />
        </button>
      ) : null}
      <div
        ref={scrollerRef}
        className="salon-scroll-x"
        style={{
          display: "flex",
          gap: 10,
          overflowX: "auto",
          padding: "2px 2px 8px",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {children}
      </div>
      {canRight ? (
        <button
          type="button"
          aria-label={ariaLabel ? `${ariaLabel}: right` : "Scroll right"}
          onClick={() => scroll(1)}
          onMouseEnter={() => startHoverScroll(1)}
          onMouseLeave={stopHoverScroll}
          onFocus={() => startHoverScroll(1)}
          onBlur={stopHoverScroll}
          style={{ ...arrowBase, right: 2 }}
        >
          <i className="las la-angle-right" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function BundleCard({ palette, bundle, lang }) {
  const { t } = useTranslation();
  const { addServices, cart } = useSalonBooking();
  const included = bundle.services.every((service) =>
    cart.some((item) => item.id === service.id),
  );
  const name = pickLocalized(bundle.name, lang);
  const description = pickLocalized(bundle.description, lang);
  const badge = pickLocalized(bundle.badge, lang);
  const price = Math.max((bundle.price || 0) - (bundle.saving || 0), 0);

  return (
    <button
      type="button"
      onClick={() => addServices(bundle.cartServices || bundle.services)}
      className="text-start"
      style={{
        minWidth: 260,
        maxWidth: 320,
        border: included ? `1.5px solid ${palette.accent}` : palette.border,
        background: included ? palette.accentSoft : palette.surface,
        borderRadius: 16,
        padding: 14,
        color: palette.text,
        cursor: "pointer",
        flex: "0 0 290px",
        boxShadow: palette.shadowSoft,
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-2">
        <span
          className="fw-700"
          style={{ fontSize: "1rem", lineHeight: 1.25, wordBreak: "break-word" }}
        >
          {name}
        </span>
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: included ? palette.accent : palette.accentSoft,
            color: included ? "#fff" : palette.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <i className={included ? "las la-check" : "las la-plus"} />
        </span>
      </div>
      <div style={{ color: palette.textMuted, fontSize: "0.84rem", marginTop: 6, lineHeight: 1.35 }}>
        {description}
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2" style={{ marginTop: 12 }}>
        {badge ? (
          <span
            style={{
              background: palette.successSoft,
              color: palette.success,
              borderRadius: 999,
              padding: "4px 9px",
              fontSize: "0.72rem",
              fontWeight: 700,
            }}
          >
            {badge}
          </span>
        ) : null}
        {bundle.saving ? (
          <span style={{ color: palette.textMuted, fontSize: "0.78rem" }}>
            {t("salonBooking.bundle.save", {
              amount: formatPrice(bundle.saving, "AMD", lang),
            })}
          </span>
        ) : null}
      </div>
      <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 12 }}>
        <span style={{ color: palette.textMuted, fontSize: "0.82rem" }}>
          <i className="las la-clock me-1" aria-hidden="true" />
          {formatDuration(bundle.duration, lang)}
        </span>
        <span className="fw-700" style={{ color: palette.text, fontVariantNumeric: "tabular-nums" }}>
          {formatPrice(price || bundle.price, "AMD", lang)}
        </span>
      </div>
    </button>
  );
}

function ServiceRow({ palette, service, lang, selected, onToggle }) {
  const { t } = useTranslation();
  const name = pickLocalized(service.name, lang);
  const description = pickLocalized(service.description, lang);
  const badge = pickLocalized(service.badge, lang);
  const mastersCount = service.masterIds?.length || 0;

  const priceLabel = service.priceFrom != null && service.price == null
    ? `${t("salonBooking.service.from")} ${formatPrice(service.priceFrom, "AMD", lang)}`
    : formatPrice(service.price ?? service.priceFrom ?? 0, "AMD", lang);

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "clamp(12px, 3vw, 16px) clamp(12px, 3vw, 18px)",
        borderRadius: 14,
        background: selected ? palette.accentSoft : palette.surface,
        border: selected
          ? `1.5px solid ${palette.accentBorder}`
          : palette.border,
        transition: "background 0.15s ease, border-color 0.15s ease",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="fw-700"
          style={{
            color: palette.text,
            fontSize: "clamp(0.95rem, 2.4vw, 1.05rem)",
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {name}
        </div>
        {description ? (
          <div
            style={{
              color: palette.textMuted,
              fontSize: "0.85rem",
              marginTop: 4,
              lineHeight: 1.4,
              wordBreak: "break-word",
            }}
          >
            {description}
          </div>
        ) : null}
        <div className="d-flex flex-wrap align-items-center gap-2" style={{ marginTop: 8 }}>
          {badge ? (
            <span
              style={{
                background: palette.successSoft,
                color: palette.success,
                borderRadius: 999,
                padding: "3px 8px",
                fontSize: "0.72rem",
                fontWeight: 700,
              }}
            >
              {badge}
            </span>
          ) : null}
          <span
            className="d-inline-flex align-items-center gap-1 fw-600"
            style={{
              color: palette.text,
              fontSize: "0.95rem",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {priceLabel}
          </span>
          <span style={{ color: palette.textSubtle, fontSize: "0.78rem" }}>•</span>
          <span
            className="d-inline-flex align-items-center gap-1"
            style={{ color: palette.textMuted, fontSize: "0.85rem" }}
          >
            <i className="las la-clock" aria-hidden="true" />
            {formatDuration(service.duration, lang)}
          </span>
          {mastersCount ? (
            <>
              <span style={{ color: palette.textSubtle, fontSize: "0.78rem" }}>•</span>
              <span style={{ color: palette.textMuted, fontSize: "0.85rem" }}>
                <i className="las la-user-check me-1" aria-hidden="true" />
                {t("salonBooking.service.mastersCount", { count: mastersCount })}
              </span>
            </>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        onClick={onToggle}
        aria-label={
          selected
            ? t("salonBooking.service.removeAria", { name })
            : t("salonBooking.service.addAria", { name })
        }
        style={{
          alignSelf: "center",
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: selected ? palette.accent : "transparent",
          color: selected ? "#fff" : palette.accent,
          border: selected ? `1px solid ${palette.accent}` : `1.5px solid ${palette.accent}`,
          cursor: "pointer",
          fontSize: "1.3rem",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s ease",
        }}
      >
        <i
          className={selected ? "las la-check" : "las la-plus"}
          aria-hidden="true"
        />
      </button>
    </div>
  );
}

function CategorySection({ palette, category, lang, openByDefault, isCartEligible }) {
  const { t } = useTranslation();
  const { cart, toggleService } = useSalonBooking();
  const [isOpen, setIsOpen] = useState(Boolean(openByDefault));

  const selectedCountInCategory = useMemo(
    () => category.services.filter((s) => cart.find((c) => c.id === s.id)).length,
    [category.services, cart],
  );

  return (
    <section
      style={{
        background: palette.surface,
        border: palette.border,
        borderRadius: 18,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-100 text-start d-flex align-items-center gap-3"
        style={{
          background: "transparent",
          border: "none",
          padding: "clamp(14px, 3vw, 18px) clamp(14px, 3vw, 20px)",
          color: palette.text,
          cursor: "pointer",
        }}
        aria-expanded={isOpen}
      >
        <span
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            background: palette.accentSoft,
            color: palette.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.4rem",
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <i className={category.icon || "las la-tag"} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            className="fw-700 d-block"
            style={{
              fontSize: "clamp(1rem, 2.6vw, 1.15rem)",
              color: palette.text,
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {pickLocalized(category.name, lang)}
          </span>
          <span style={{ fontSize: "0.85rem", color: palette.textMuted }}>
            {pickLocalized(category.description, lang) ||
              t("salonBooking.catalog.servicesCount", { count: category.services.length })}
            {selectedCountInCategory > 0 ? (
              <>
                {" • "}
                <span style={{ color: palette.accent, fontWeight: 600 }}>
                  {t("salonBooking.catalog.selectedCount", { count: selectedCountInCategory })}
                </span>
              </>
            ) : null}
          </span>
        </span>
        <i
          className="las la-angle-down"
          style={{
            color: palette.textMuted,
            fontSize: "1.2rem",
            transition: "transform 0.2s ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
          aria-hidden="true"
        />
      </button>

      {isOpen ? (
        <div
          style={{
            padding: "0 clamp(10px, 2.5vw, 14px) clamp(12px, 2.5vw, 16px)",
            display: "grid",
            gap: 8,
          }}
        >
          {!isCartEligible ? (
            <div
              style={{
                fontSize: "0.82rem",
                color: palette.warning,
                background: "hsla(40, 90%, 60%, 0.12)",
                border: "1px solid hsla(40, 90%, 60%, 0.3)",
                borderRadius: 12,
                padding: "8px 12px",
              }}
            >
              <i className="las la-info-circle me-1" aria-hidden="true" />
              {t("salonBooking.catalog.differentMasterWarning")}
            </div>
          ) : null}

          {category.services.map((service) => {
            const selected = Boolean(cart.find((c) => c.id === service.id));
            return (
              <ServiceRow
                key={service.id}
                palette={palette}
                service={service}
                lang={lang}
                selected={selected}
                onToggle={() => toggleService(service)}
              />
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

/**
 * `categories`: array from mockSalonData / future backend response.
 *
 * `isCartEligible(category)` lets the parent flag categories whose
 * services can't be combined with the current cart (different master pool
 * required). We display a soft warning but keep them clickable so the user
 * isn't blocked from swapping their selection.
 */
export function ServiceCatalog({ palette, lang, categories, bundles = [], isCartEligible }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const filtered = useMemo(() => {
    const scoped = activeCategory === "all"
      ? categories
      : categories.filter((category) => category.id === activeCategory);
    if (!search.trim()) return scoped;
    const q = search.toLowerCase().trim();
    return scoped
      .map((category) => {
        const matched = category.services.filter((service) => {
          const name = pickLocalized(service.name, lang).toLowerCase();
          const desc = pickLocalized(service.description, lang).toLowerCase();
          return name.includes(q) || desc.includes(q);
        });
        if (matched.length === 0) return null;
        return { ...category, services: matched };
      })
      .filter(Boolean);
  }, [categories, search, lang, activeCategory]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <style>
        {`
          .salon-scroll-x {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .salon-scroll-x::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {bundles.length > 0 ? (
        <section style={{ display: "grid", gap: 10 }}>
          <div className="d-flex align-items-center justify-content-between gap-2">
            <div>
              <div className="fw-700" style={{ color: palette.text, fontSize: "1.05rem" }}>
                {t("salonBooking.bundle.title")}
              </div>
              <div style={{ color: palette.textMuted, fontSize: "0.86rem" }}>
                {t("salonBooking.bundle.subtitle")}
              </div>
            </div>
          </div>
          <HorizontalScrollArea
            palette={palette}
            ariaLabel={t("salonBooking.bundle.title")}
          >
            {bundles.map((bundle) => (
              <div key={bundle.id} style={{ scrollSnapAlign: "start" }}>
                <BundleCard palette={palette} bundle={bundle} lang={lang} />
              </div>
            ))}
          </HorizontalScrollArea>
        </section>
      ) : null}

      <HorizontalScrollArea
        palette={palette}
        ariaLabel={t("salonBooking.catalog.title")}
      >
        {[{ id: "all", name: { hy: "Բոլորը", en: "All" }, icon: "las la-border-all" }, ...categories].map((category) => {
          const selected = activeCategory === category.id;
          return (
            <button
              key={category.id}
              type="button"
              onClick={() => setActiveCategory(category.id)}
              className="fw-700"
              style={{
                flexShrink: 0,
                border: selected ? `1.5px solid ${palette.accent}` : palette.border,
                background: selected ? palette.accent : palette.surface,
                color: selected ? "#fff" : palette.text,
                borderRadius: 999,
                padding: "9px 13px",
                fontSize: "0.86rem",
                cursor: "pointer",
              }}
            >
              <i className={`${category.icon || "las la-tag"} me-1`} aria-hidden="true" />
              {pickLocalized(category.name, lang)}
            </button>
          );
        })}
      </HorizontalScrollArea>

      <div
        className="d-flex align-items-center"
        style={{
          background: palette.surface,
          border: palette.border,
          borderRadius: 14,
          padding: "10px 16px",
        }}
      >
        <i
          className="las la-search"
          style={{
            color: palette.textMuted,
            fontSize: "1.2rem",
            marginRight: 10,
          }}
          aria-hidden="true"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("salonBooking.catalog.searchPlaceholder")}
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
            color: palette.text,
            flex: 1,
            fontSize: "0.95rem",
          }}
        />
        {search ? (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label={t("salonBooking.catalog.clearSearch")}
            style={{
              background: "transparent",
              border: "none",
              color: palette.textMuted,
              cursor: "pointer",
              fontSize: "1rem",
              padding: 0,
            }}
          >
            <i className="las la-times" />
          </button>
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <div
          style={{
            background: palette.surface,
            border: palette.border,
            borderRadius: 16,
            padding: "32px 16px",
            textAlign: "center",
            color: palette.textMuted,
          }}
        >
          <i
            className="las la-search-minus"
            style={{ fontSize: "2rem", color: palette.accent, display: "block", marginBottom: 8 }}
            aria-hidden="true"
          />
          <div className="fw-600" style={{ color: palette.text }}>
            {t("salonBooking.catalog.noResults")}
          </div>
          <div style={{ fontSize: "0.9rem", marginTop: 4 }}>
            {t("salonBooking.catalog.noResultsHint")}
          </div>
        </div>
      ) : (
        filtered.map((category, idx) => (
          <CategorySection
            key={category.id}
            palette={palette}
            category={category}
            lang={lang}
            openByDefault={idx === 0 || Boolean(search.trim())}
            isCartEligible={isCartEligible ? isCartEligible(category) : true}
          />
        ))
      )}
    </div>
  );
}
